/**
 * Hook for exporting CRM objects to CSV.
 *
 * Provides functionality to export CRM objects with optional filters,
 * handling the file download via a blob response.
 */

import { useCallback, useState } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';
import { sanitizeError } from '@/lib/utils/errors';
import type { FilterGroup } from '@/types/crm';

export type ExportColumn =
  | string
  | { association: string; target_property: string };

interface ExportOptions {
  entity_type_name: string;
  filter_groups?: FilterGroup[];
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  search?: string;
  search_properties?: string[];
  /** Entity IDs to exclude (for "all except these" mode). Mutually exclusive with entity_ids. */
  excluded_entity_ids?: string[];
  /** Entity IDs to include (for individual selection mode). Mutually exclusive with excluded_entity_ids. */
  entity_ids?: string[];
  /** Ordered visible columns to export: own-property names (string) or lookup descriptors. Absent/empty → server exports the default full set. */
  columns?: ExportColumn[];
  /**
   * Filename for the download, overriding the server's.
   *
   * The server names files after the entity type alone (`Deals_2026-09-07.csv`),
   * which is right for a full export but not for a filtered one — a caller that
   * exports a slice usually knows something about that slice the server does
   * not (which chart segment, which saved view). Naming it here rather than
   * passing a hint to the server also keeps client text out of the
   * `Content-Disposition` header, which is a header-injection surface.
   *
   * Omit to use the server's name.
   */
  filename?: string;
}

interface UseCrmExportReturn {
  /**
   * Export CRM objects to CSV and trigger download.
   */
  exportObjects: (options: ExportOptions) => Promise<void>;
  /**
   * Whether an export is currently in progress.
   */
  isExporting: boolean;
  /**
   * Error message from the last export attempt, if any.
   */
  error: string | null;
}

/**
 * Hook for exporting CRM objects to CSV.
 *
 * @param orgId - Organization ID for the export
 * @returns Export function, loading state, and error state
 *
 * @example
 * ```tsx
 * const { exportObjects, isExporting, error } = useCrmExport(orgId);
 *
 * // Export all contacts
 * await exportObjects({ entity_type_name: 'ahoy_contact' });
 *
 * // Export filtered contacts
 * await exportObjects({
 *   entity_type_name: 'ahoy_contact',
 *   filter_groups: [{ filters: [{ property: 'ahoy_email', operator: 'contains', value: '@example.com' }] }],
 * });
 * ```
 */
export function useCrmExport(orgId: number): UseCrmExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportObjects = useCallback(
    async (options: ExportOptions): Promise<void> => {
      if (!orgId) {
        setError('Organization ID is required');
        return;
      }

      setIsExporting(true);
      setError(null);

      try {
        const url = `/api/orgs/${orgId}/crm-events/export/`;

        // Build request body - only include filter_groups if not empty
        const body: Record<string, unknown> = {
          entity_type_name: options.entity_type_name,
        };

        if (options.filter_groups && options.filter_groups.length > 0) {
          body.filter_groups = options.filter_groups;
        }

        if (options.sort_by) {
          body.sort_by = options.sort_by;
          body.sort_order = options.sort_order || 'asc';
        }

        if (options.search) {
          body.search = options.search;
        }

        if (options.search_properties && options.search_properties.length > 0) {
          body.search_properties = options.search_properties;
        }

        if (
          options.entity_ids &&
          options.entity_ids.length > 0 &&
          options.excluded_entity_ids &&
          options.excluded_entity_ids.length > 0
        ) {
          throw new Error(
            'entity_ids and excluded_entity_ids are mutually exclusive'
          );
        }

        if (
          options.excluded_entity_ids &&
          options.excluded_entity_ids.length > 0
        ) {
          body.excluded_entity_ids = options.excluded_entity_ids;
        }

        if (options.entity_ids && options.entity_ids.length > 0) {
          body.entity_ids = options.entity_ids;
        }

        if (options.columns && options.columns.length > 0) {
          body.columns = options.columns;
        }

        const response = await fetchWithDefaults(
          url,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          },
          300000
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const rawError =
            errorData.error || errorData.detail || 'Export failed';
          throw new Error(sanitizeError(rawError));
        }

        // Filename precedence: the caller's, then the server's, then a
        // generic fallback.
        //
        // Note the header is only readable because `CORS_EXPOSE_HEADERS`
        // names Content-Disposition (ahoy/settings.py) — the SPA and API are
        // separate origins, so without that every export silently landed on
        // the 'export.csv' fallback below no matter what the server sent.
        let filename = options.filename ?? 'export.csv';
        if (!options.filename) {
          const contentDisposition = response.headers.get(
            'Content-Disposition'
          );
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(
              /filename="?([^";\n]+)"?/
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1];
            }
          }
        }

        // Convert response to blob and trigger download
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);

        // Create temporary anchor element to trigger download
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();

        // Cleanup
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(downloadUrl);
      } catch (caughtError) {
        console.error(
          'CRM export failed',
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError)),
          { orgId }
        );
        setError(
          "We couldn't export your data. Try again, and get in touch if it keeps happening."
        );
        throw caughtError;
      } finally {
        setIsExporting(false);
      }
    },
    [orgId]
  );

  return {
    exportObjects,
    isExporting,
    error,
  };
}
